import axios from 'axios';
import { applyTz, createUri, Api } from './utils';

import type { InvoicedEntity } from '@/types/company';

const DATA_URL = createUri(Api.Orgs, '/org/V1');

export type IdMap = {
    [id: string]: string;
};

export type NameMap = {
    companies: IdMap;
    resellers: IdMap;
    locations: IdMap;
};

export type OrgMap = {
    companyLocationMap: Record<string, number[]>;
    companyResellerMap: Record<string, number[]>;
    resellerLocationMap: Record<string, number[]>;
};

export type LocationMap = Record<string, { resellerId: number | null; companyId: number }>;

export enum OrgUrls {
    Countries = 'countries',
    NameMap = 'nameMap',
    OrgMap = 'orgMap',
    LocationMap = 'locationMap',
    Company = 'company',
    Location = 'location',
    Reseller = 'reseller',
    BCCustomers = 'businessCentral/customers',
    LocationEmployees = 'location/employees',
    LocationEmployeesCSV = 'location/employees/csv',
    EmployeeRanks = 'location/employeeRanks',
    EmployeeCutMaterials = 'employee/materials',
    SearchLocations = 'location/search',
    SearchResellers = 'reseller/search',
    SearchCompanies = 'company/search',
    ProductCatalog = 'productCatalog',
    ProductCatalogs = 'productCatalogs',
    ProductCatalogOrder = 'productCatalogs/order',
    BusinessCentralProducts = 'businessCentral/products',
}

export type Countries = {
    countryName: string;
}[];

export type LocationResponse = {
    LocationID?: number;
    CompanyID: number;
    LocationName: string;
    PIN: string;
    Inactive: boolean;
    Country: string;
    ConsentToSell: string | null;
    CutPlanID: number;
    WarrantyPlanID: number;
    TrackingFlag: number;
    WalletTemplate: string | null;
};

export type CompanyResponse = {
    CompanyID?: number;
    CompanyName: string;
    Inactive: boolean;
    CustomerID?: string | null;
    SalesRepTrackingEnabled: boolean | null;
} & (
    | {
          Reseller: true;
          ResellerCompanyID: number;
      }
    | { Reseller: false; ResellerCompanyID?: number }
) &
    InvoicedEntity;

export type EmployeeResponse = {
    Email: string;
    Name: string;
    LocationID: number;
    ResellerID: number;
    CompanyID: number;
    Status: string;
    TotalCuts: number;
    WarrantyClaimCuts: number;
    SuccessfulCuts: number;
    EmployeeRank: number;
    WarrantyClaimPercentage: number;
};

export const orgApi = axios.create({
    baseURL: DATA_URL,
});

export const fetchOrgData = async <T>(
    token: string,
    url: OrgUrls,
    params?: Record<string, string | number | undefined>,
    tz?: string,
) => {
    const resp = await orgApi.get<T>(url, {
        params: applyTz(params, tz),
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    return resp.data;
};

export const fetchOrgGeneric =
    <T>(url: OrgUrls) =>
    async (token: string) => {
        const { data } = await orgApi.get<T>(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        return data;
    };
